import { redirect } from "@tanstack/react-router";
import { type Role, toRole } from "@/components/users/roles";
import { ROLE_RANK } from "@/stores/auth-store";

interface SessionLike {
  user?: { role?: string | null } | null;
}

/**
 * Where a denied user is sent. Defaults to `/dashboard/inspections` — the
 * universal floor reachable by every authenticated role (see the comment in
 * `routes/dashboard/inspections/route.tsx`). Admin-only routes pass
 * `/no-access` instead so a non-admin gets an explicit "not authorised"
 * signal rather than a silent bounce onto an unrelated list (CTE-127).
 */
type RedirectTarget = "/dashboard/inspections" | "/no-access";

/**
 * Guard for TanStack Router `beforeLoad`. Throws a silent redirect when the
 * session role doesn't meet `minRole`. The redirect target defaults to
 * `/dashboard/inspections`; pass `redirectTo: "/no-access"` on admin-only
 * routes. Use the parent dashboard route's `context.session` as input.
 */
export function requireMinRole(
  session: SessionLike | null | undefined,
  minRole: Role,
  redirectTo: RedirectTarget = "/dashboard/inspections",
) {
  const sessionRole = session?.user?.role;
  if (!sessionRole) {
    throw redirect({ to: redirectTo });
  }
  const role = toRole(sessionRole);
  if (ROLE_RANK[role] < ROLE_RANK[minRole]) {
    throw redirect({ to: redirectTo });
  }
}
